import { useNode } from "@craftjs/core";
import { parseISO } from "date-fns";
import { format, zonedTimeToUtc } from "date-fns-tz";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { EventDetails } from "src/types";
import styled from "styled-components";

import { Select } from "@/components/shared/forms";
import { Spinner } from "@/components/shared/spinner";
import {
  getAllSeries,
  getIsGivenSeriesEventsLoadingStatus,
  getSeriesEvents,
} from "@/features/related-events/related-events-reducer";
import logger from "@/utils/logger";

const formatEventDate = (dateString: string) => {
  try {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startUtc = zonedTimeToUtc(parseISO(dateString), userTimeZone);
    return [
      format(startUtc, "MMMM dd, yyyy • hh:mm aa", {
        timeZone: userTimeZone,
      }),

      format(startUtc, "zzz", {
        timeZone: userTimeZone,
      }),
    ];
  } catch (error) {
    logger.error(error, {
      details: "Failed to parse start/end time in Related Events block",
    });

    return [];
  }
};

export default function RelatedEventsSettings() {
  const {
    props,
    actions: { setProp },
    nodeId,
  } = useNode((node) => {
    return {
      nodeId: node.id,
      props: node.data.props,
    };
  });

  const allSeries = useSelector(getAllSeries);
  const seriesEvents = useSelector(getSeriesEvents);
  const seriesEventsLoadingStatus = useSelector((state) =>
    getIsGivenSeriesEventsLoadingStatus(state, props.seriesExternalId)
  );

  const { t } = useTranslation();

  React.useEffect(() => {
    if (
      !allSeries.some((series) => series.externalId === props.seriesExternalId)
    ) {
      setProp(
        (props: { seriesExternalId: string }) =>
          (props.seriesExternalId = allSeries[0].externalId)
      );
    }
  }, [nodeId, setProp, allSeries, props.seriesExternalId]);

  const options = allSeries.map((series) => ({
    label: series.name,
    value: series.externalId,
  }));

  return (
    <>
      <div className="flex flex-col justify-between">
        <span className="mb-2 text-sm font-medium leading-6">
          {t("event-series.related-events")}
        </span>
        <Select
          name="event-series"
          options={options}
          label="Event series"
          onChange={(value) => {
            setProp((props: { seriesExternalId: string }) => {
              props.seriesExternalId = value;
            });
          }}
          value={props.seriesExternalId}
        />
        {seriesEventsLoadingStatus === "loading" && (
          <Wrapper>
            <Spinner size="large" />
          </Wrapper>
        )}
        {seriesEventsLoadingStatus === "loaded" && (
          <span className="mt-4 text-sm font-medium leading-6">
            {t("event-series.featured-in-series")}
          </span>
        )}
      </div>
      <div className="flex flex-col justify-between">
        {(seriesEvents[props.seriesExternalId] || []).map(
          (event: EventDetails) => (
            <EventCard key={event.external_id}>
              <span className="text-sm leading-6">{event.name}</span>
              <div className="flex">
                <span className="mr-1 text-xxs leading-4 text-gray-600">
                  {formatEventDate(event.time_start)[0]}
                </span>
                <span className="text-xxs leading-4 text-gray-400">
                  {formatEventDate(event.time_start)[1]}
                </span>
              </div>
            </EventCard>
          )
        )}
      </div>
    </>
  );
}

const EventCard = styled.div`
  border: 1px solid #e4e6eb;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 12px;
  padding: 8px 12px;
  margin-top: 8px;
`;

const Wrapper = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
